import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import {
  TrendingUp,
  Users,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Bell,
  Award,
  Star,
  Crown,
  Trophy,
  User,
  DollarSign,
  ArrowRight,
  Sparkles,
  AlertCircle,
} from 'lucide-react';

const RANK_CONFIG = {
  neg: { label: 'NEG', color: 'bg-blue-500', icon: User },
  rm: { label: 'RM', color: 'bg-purple-500', icon: Award },
  avp: { label: 'AVP', color: 'bg-pink-500', icon: Star },
  svp: { label: 'SVP', color: 'bg-orange-500', icon: Crown },
  evp: { label: 'EVP', color: 'bg-red-500', icon: Trophy },
};

const AdminPromotions = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [eligibleAgents, setEligibleAgents] = useState([]);
  const [promotionRequests, setPromotionRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('eligible');
  const [checking, setChecking] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [eligibleRes, requestsRes] = await Promise.all([
        api.get('/promotions/eligible'),
        api.get('/promotions/requests'),
      ]);
      setEligibleAgents(eligibleRes.data.agents || []);
      setPromotionRequests(requestsRes.data || []);
    } catch (error) {
      console.error('Error loading promotion data:', error);
      toast.error('Failed to load promotion data');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckAndNotify = async () => {
    setChecking(true);
    try {
      const res = await api.post('/promotions/check-and-notify');
      toast.success(`${res.data.new_requests} new promotion requests created and agents notified`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check eligibility');
    } finally {
      setChecking(false);
    }
  };

  const handleApprove = async (requestId) => {
    setProcessing(prev => ({ ...prev, [requestId]: 'approving' }));
    try {
      const res = await api.post(`/promotions/${requestId}/approve`);
      toast.success(`Promotion approved! ${res.data.agent_id} is now ${res.data.new_rank_display}`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve promotion');
    } finally {
      setProcessing(prev => ({ ...prev, [requestId]: null }));
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;
    
    setProcessing(prev => ({ ...prev, [selectedRequest.id]: 'rejecting' }));
    try {
      await api.post(`/promotions/${selectedRequest.id}/reject?reason=${encodeURIComponent(rejectReason)}`);
      toast.success('Promotion request rejected');
      setRejectDialogOpen(false);
      setSelectedRequest(null);
      setRejectReason('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject promotion');
    } finally {
      setProcessing(prev => ({ ...prev, [selectedRequest?.id]: null }));
    }
  };

  const openRejectDialog = (request) => {
    setSelectedRequest(request);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const pendingRequests = promotionRequests.filter(r => r.status === 'pending');
  const approvedRequests = promotionRequests.filter(r => r.status === 'approved');
  const rejectedRequests = promotionRequests.filter(r => r.status === 'rejected');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="admin-promotions-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-purple-600" />
            Auto-Promote Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Review and approve agent promotions based on rank requirements
          </p>
        </div>
        <Button 
          className="bg-purple-600 hover:bg-purple-700"
          onClick={handleCheckAndNotify}
          disabled={checking}
          data-testid="check-notify-btn"
        >
          {checking ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Bell className="w-4 h-4 mr-2" />
          )}
          {checking ? 'Checking...' : 'Check & Notify Eligible'}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Eligible for Promotion</p>
                <p className="text-2xl font-bold text-green-600">{eligibleAgents.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Pending Approval</p>
                <p className="text-2xl font-bold text-yellow-600">{pendingRequests.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Approved</p>
                <p className="text-2xl font-bold text-blue-600">{approvedRequests.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Rejected</p>
                <p className="text-2xl font-bold text-gray-600">{rejectedRequests.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="eligible" className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            Eligible ({eligibleAgents.length})
          </TabsTrigger>
          <TabsTrigger value="pending" className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Pending ({pendingRequests.length})
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            History ({approvedRequests.length + rejectedRequests.length})
          </TabsTrigger>
        </TabsList>

        {/* Eligible Agents */}
        <TabsContent value="eligible">
          {eligibleAgents.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No Eligible Agents</h3>
                <p className="text-gray-500 mt-1">No agents currently meet all requirements for promotion</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {eligibleAgents.map((item) => {
                const currentRankConfig = RANK_CONFIG[item.agent.current_rank] || RANK_CONFIG.neg;
                const nextRankConfig = RANK_CONFIG[item.agent.next_rank] || RANK_CONFIG.rm;
                const CurrentIcon = currentRankConfig.icon;
                const NextIcon = nextRankConfig.icon;
                
                return (
                  <Card key={item.agent.id} className="overflow-hidden">
                    <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-4 text-white">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-xl font-bold">
                            {item.agent.full_name?.charAt(0)?.toUpperCase()}
                          </div>
                          <div>
                            <h3 className="font-bold">{item.agent.full_name}</h3>
                            <p className="text-white/80 text-sm">{item.agent.agent_code}</p>
                          </div>
                        </div>
                        {item.has_pending_request ? (
                          <Badge className="bg-yellow-500 text-white">Request Pending</Badge>
                        ) : (
                          <Badge className="bg-white/20 text-white">Ready</Badge>
                        )}
                      </div>
                    </div>
                    
                    <CardContent className="p-4 space-y-4">
                      {/* Rank Progression */}
                      <div className="flex items-center justify-center gap-4">
                        <div className="text-center">
                          <div className={`w-12 h-12 rounded-full ${currentRankConfig.color} flex items-center justify-center text-white mx-auto`}>
                            <CurrentIcon className="w-6 h-6" />
                          </div>
                          <p className="text-sm font-medium mt-1">{currentRankConfig.label}</p>
                        </div>
                        <ArrowRight className="w-6 h-6 text-green-500" />
                        <div className="text-center">
                          <div className={`w-12 h-12 rounded-full ${nextRankConfig.color} flex items-center justify-center text-white mx-auto ring-4 ring-green-200`}>
                            <NextIcon className="w-6 h-6" />
                          </div>
                          <p className="text-sm font-medium mt-1">{item.agent.next_rank_display}</p>
                        </div>
                      </div>

                      {/* Metrics */}
                      <div className="space-y-2 bg-gray-50 rounded-lg p-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500 flex items-center gap-1">
                            <DollarSign className="w-3 h-3" /> Personal Sales
                          </span>
                          <span className="text-green-600 font-medium">
                            RM {item.metrics.personal_sales.current.toLocaleString()} / {item.metrics.personal_sales.required.toLocaleString()}
                          </span>
                        </div>
                        {item.metrics.team_commission.required > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500 flex items-center gap-1">
                              <Users className="w-3 h-3" /> Team Commission
                            </span>
                            <span className="text-green-600 font-medium">
                              RM {item.metrics.team_commission.current.toLocaleString()} / {item.metrics.team_commission.required.toLocaleString()}
                            </span>
                          </div>
                        )}
                        {item.metrics.downlines.required > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500 flex items-center gap-1">
                              <Users className="w-3 h-3" /> {item.metrics.downlines.required_rank?.toUpperCase() || ''} Downlines
                            </span>
                            <span className="text-green-600 font-medium">
                              {item.metrics.downlines.current} / {item.metrics.downlines.required}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          className="flex-1"
                          onClick={() => navigate(`/admin/agents/${item.agent.id}`)}
                        >
                          View Profile
                        </Button>
                        {item.has_pending_request ? (
                          <Button 
                            className="flex-1 bg-yellow-500 hover:bg-yellow-600"
                            onClick={() => {
                              setActiveTab('pending');
                            }}
                          >
                            View Request
                          </Button>
                        ) : (
                          <Button 
                            className="flex-1 bg-green-600 hover:bg-green-700"
                            onClick={handleCheckAndNotify}
                          >
                            Create Request
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Pending Requests */}
        <TabsContent value="pending">
          {pendingRequests.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Clock className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No Pending Requests</h3>
                <p className="text-gray-500 mt-1">All promotion requests have been processed</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {pendingRequests.map((request) => {
                const currentRankConfig = RANK_CONFIG[request.current_rank] || RANK_CONFIG.neg;
                const nextRankConfig = RANK_CONFIG[request.next_rank] || RANK_CONFIG.rm;
                const isProcessing = processing[request.id];
                
                return (
                  <Card key={request.id} className="border-l-4 border-l-yellow-500">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div 
                            className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-xl"
                            style={{ backgroundColor: `hsl(${request.agent_name?.charCodeAt(0) * 10 % 360}, 70%, 50%)` }}
                          >
                            {request.agent_name?.charAt(0)?.toUpperCase()}
                          </div>
                          <div>
                            <h3 className="font-bold text-lg">{request.agent_name}</h3>
                            <p className="text-gray-500 text-sm">{request.agent_code}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge className={`${currentRankConfig.color} text-white`}>
                                {currentRankConfig.label}
                              </Badge>
                              <ArrowRight className="w-4 h-4 text-gray-400" />
                              <Badge className={`${nextRankConfig.color} text-white`}>
                                {request.next_rank_display}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <div className="text-right text-sm text-gray-500 mr-4">
                            <p>Submitted</p>
                            <p className="font-medium text-gray-700">
                              {new Date(request.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => openRejectDialog(request)}
                            disabled={isProcessing}
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Reject
                          </Button>
                          <Button
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleApprove(request.id)}
                            disabled={isProcessing}
                            data-testid={`approve-btn-${request.id}`}
                          >
                            {isProcessing === 'approving' ? (
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <CheckCircle className="w-4 h-4 mr-2" />
                            )}
                            Approve
                          </Button>
                        </div>
                      </div>

                      {/* Metrics Snapshot */}
                      {request.metrics_snapshot && (
                        <div className="mt-4 grid grid-cols-3 gap-4 bg-gray-50 rounded-lg p-3">
                          <div className="text-center">
                            <p className="text-xs text-gray-500">Personal Sales</p>
                            <p className="font-semibold text-green-600">
                              RM {request.metrics_snapshot.personal_sales?.current?.toLocaleString() || 0}
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-500">Team Commission</p>
                            <p className="font-semibold text-green-600">
                              RM {request.metrics_snapshot.team_commission?.current?.toLocaleString() || 0}
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-500">Qualified Downlines</p>
                            <p className="font-semibold text-green-600">
                              {request.metrics_snapshot.downlines?.current || 0}
                            </p>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* History */}
        <TabsContent value="history">
          {approvedRequests.length === 0 && rejectedRequests.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <CheckCircle className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No History</h3>
                <p className="text-gray-500 mt-1">No promotions have been processed yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {[...approvedRequests, ...rejectedRequests]
                .sort((a, b) => new Date(b.approved_at || b.rejected_at) - new Date(a.approved_at || a.rejected_at))
                .map((request) => {
                  const isApproved = request.status === 'approved';
                  
                  return (
                    <Card 
                      key={request.id} 
                      className={`border-l-4 ${isApproved ? 'border-l-green-500' : 'border-l-red-500'}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div 
                              className={`w-12 h-12 rounded-full flex items-center justify-center ${isApproved ? 'bg-green-100' : 'bg-red-100'}`}
                            >
                              {isApproved ? (
                                <CheckCircle className="w-6 h-6 text-green-600" />
                              ) : (
                                <XCircle className="w-6 h-6 text-red-600" />
                              )}
                            </div>
                            <div>
                              <h3 className="font-bold">{request.agent_name}</h3>
                              <p className="text-gray-500 text-sm">{request.agent_code}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline">{request.current_rank?.toUpperCase()}</Badge>
                                <ArrowRight className="w-4 h-4 text-gray-400" />
                                <Badge className={isApproved ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                                  {request.next_rank_display}
                                </Badge>
                              </div>
                            </div>
                          </div>
                          
                          <div className="text-right">
                            <Badge className={isApproved ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                              {isApproved ? 'Approved' : 'Rejected'}
                            </Badge>
                            <p className="text-sm text-gray-500 mt-1">
                              {new Date(request.approved_at || request.rejected_at).toLocaleDateString()}
                            </p>
                            {request.rejection_reason && (
                              <p className="text-sm text-red-600 mt-1 max-w-xs truncate">
                                Reason: {request.rejection_reason}
                              </p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              Reject Promotion Request
            </DialogTitle>
            <DialogDescription>
              {selectedRequest && (
                <>
                  Are you sure you want to reject {selectedRequest.agent_name}'s promotion to {selectedRequest.next_rank_display}?
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium text-gray-700">Reason (optional)</label>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Provide a reason for rejection..."
              className="mt-2"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-red-600 hover:bg-red-700"
              onClick={handleReject}
              disabled={processing[selectedRequest?.id]}
            >
              {processing[selectedRequest?.id] === 'rejecting' ? 'Rejecting...' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPromotions;
